import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import useNumberTransform from '../hooks/useNumberTransform';
import useMeasure from 'use-measure';
import styled from 'styled-components/macro';
import { motion } from 'framer-motion';

const AnimatedNumber = forwardRef(
  (
    {
      value,
      integer = false,
      animate = true,
      percentage = false,
      decimals = 2,
      formatter,
      ...rest
    },
    ref
  ) => {
    const v = useMemo(() => {
      let _value = `${value}`;
      if (percentage && _value.includes('%'))
        _value = _value.replace(',', '.').replace('%', '');
      return Number(_value);
    }, [percentage, value]);
    const ghostRef = useRef(null);
    const iRef = useRef(null);
    const prevRef = useRef(null);
    const gD = useMeasure(ghostRef);
    const iD = useMeasure(iRef);
    const pD = useMeasure(prevRef);
    const [prevValue, setPreviousValue] = useState(0);
    const cb = useCallback(v => setPreviousValue(v), [setPreviousValue]);
    const [animatedNumber] = useNumberTransform(v, { integer, animate, cb });

    const _formatter = useCallback(
      n => {
        if (!integer) n = n.toFixed(decimals);
        if (formatter && typeof formatter === 'function') n = formatter(n);
        if (percentage) n = `${n}%`.replace('.', ',');
        return n;
      },
      [decimals, formatter, integer, percentage]
    );

    const dimensions = useMemo(() => {
      let d;
      if (prevValue < v) d = gD;
      else if (prevValue > v) d = pD;
      else d = iD;
      return {
        width: d.width,
        height: d.height,
      };
    }, [gD, iD, pD, prevValue, v]);

    return (
      <>
        <Span ref={ref} animate={dimensions} {...rest}>
          <I className="hide" ref={ghostRef}>
            {_formatter(v)}
          </I>
          <I className="hide" ref={prevRef}>
            {_formatter(prevValue)}
          </I>
          <I ref={iRef}>{_formatter(animatedNumber)}</I>
        </Span>
      </>
    );
  }
);

const Span = styled(motion.span)`
  min-width: 1;
  position: relative;
  display: inline-block;
  text-align: end;
  font-feature-settings: 'tnum';
`;

const I = styled.i`
  position: absolute;
  top: 0;
  right: 0;
  &.hide {
    opacity: 0;
    pointer-events: none;
  }
`;

AnimatedNumber.displayName = 'AnimatedNumber';

export default AnimatedNumber;

// numero previo es más pequeño que el actual
// numero previo es mas grande que el actual, y ha terminado la animación
